import React, { FC, useEffect, useState } from 'react'
import { ChevronRight } from '@mui/icons-material'

// Components
import { PaymentCardItem } from '../PaymentCardItem/PaymentCardItem'
import { getPaymentMethodsFormatted, getPaymentRequestStatus } from '../../../helpers/payment'
import { IconCreditCard } from '../../icons/IconCreditCard'
import { IconBank } from '../../icons/IconBank'
import { Skeleton } from '../../ui/skeleton'

import { useAppSelector } from '../../../store/hooks'
import { selectProfileData } from '../../../store/user/userSlice'

// Types
import {
  IPaymentsCards,
  ISelectedCard,
  IUserACH,
  IUserCard,
  IUserInterface,
} from '../../../models/users/user.interface'
import { IPaymentMethodOptions } from '../../../types/payment'

interface AddNewProps {
  title: string
  onClick: () => void
  icon: any
}

const AddNew: FC<AddNewProps> = ({ icon, title, onClick }) => {
  const Icon = icon
  return (
    <div className="group mb-3 relative cursor-pointer flex items-center">
      <Icon className="absolute z-10 left-4 top-1/2 -translate-y-1/2 !max-w-[22px] !max-h-[19px] !w-full !h-full object-contain fill-SeabiscuitDark200ThemeColor group-hover:fill-SeabiscuitMainThemeColor transition-all" />
      <div
        className="w-full py-4 pl-14 pr-2.5 border border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor text-sm rounded-lg focus:border-blue-500  cursor-pointer hover:bg-SeabiscuitMainThemeColor/5 hover:border-SeabiscuitMainThemeColor/5 hover:text-SeabiscuitMainThemeColor transition-all"
        onClick={onClick}
      >
        {title}
      </div>
      <ChevronRight className="absolute z-10 right-4 top-1/2 -translate-y-1/2 text-SeabiscuitDark200ThemeColor group-hover:text-SeabiscuitMainThemeColor transition-all" />
    </div>
  )
}
interface CreditCardPaymentMethodProps {
  availablePaymentMethods: ('cards' | 'ach')[]
  selectedPaymentMethod: IPaymentMethodOptions | null
  selectedCard: ISelectedCard
  setSelectedCard: (value: ISelectedCard) => void
  user: IUserInterface | null
  organizer: IUserInterface | null
  openAddNewCardModal: () => void
  openAddNewBankModal?: () => void
}

export const CreditCardPaymentMethod: FC<CreditCardPaymentMethodProps> = ({
  availablePaymentMethods,
  selectedPaymentMethod,
  selectedCard,
  setSelectedCard,
  user,
  organizer,
  openAddNewCardModal,
  openAddNewBankModal,
}) => {
  const profileData = useAppSelector(selectProfileData)

  const [loading, setLoading] = useState(true)
  const [paymentMethods, setPaymentMethods] = useState<IPaymentsCards>({ CARDS: [], ACH: [] })
  const [userCards, setUserCards] = useState<IUserCard[]>([])
  const [userACHs, setUserACHs] = useState<IUserACH[]>([])

  useEffect(() => {
    const getPaymentMethods = async (
      userStripeId: string | undefined,
      userEmail: string,
      userStripeAccountId: string | null | undefined
    ) => {
      setLoading(true)

      if (!userStripeId || !userStripeAccountId) {
        setLoading(false)
        return
      }

      const paymentsRes = await getPaymentMethodsFormatted(userEmail, userStripeAccountId)

      setPaymentMethods(paymentsRes)
      setLoading(false)
    }
    if (user?.id && organizer?.id)
      getPaymentMethods(user.userStripeId, user.userEmail, organizer.userStripeAccountId).then()
  }, [organizer?.id, organizer?.userStripeAccountId, user?.id, user?.userEmail, user?.userStripeId])

  useEffect(() => {
    if (availablePaymentMethods.includes('cards')) {
      setUserCards(paymentMethods.CARDS)
    }
    if (availablePaymentMethods.includes('ach')) {
      setUserACHs(paymentMethods.ACH)
    }
  }, [paymentMethods, availablePaymentMethods])

  useEffect(() => {
    if (user?.userStripeDefaultPmId) {
      const defaultCard = [...paymentMethods.CARDS, ...paymentMethods.ACH].find(
        (payment) => payment.pmId === user.userStripeDefaultPmId
      )
      if (defaultCard?.pmId && defaultCard.customerId === profileData?.userStripeId) {
        setSelectedCard({
          type: defaultCard.type,
          pmId: defaultCard.pmId,
          cardNumber: defaultCard.cardNumber,
        })
      }
    }
  }, [paymentMethods, profileData?.userStripeId, user?.userStripeDefaultPmId, setSelectedCard])

  if (loading) {
    return (
      <div className="relative space-y-2">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    )
  }

  return (
    <>
      {[...userCards, ...userACHs]?.map((card, index) => {
        if (card.pmId) {
          let paymentRequestStatus: 'approved' | 'pending' | 'rejected' | null

          if (
            card.customerId === organizer?.userStripeId ||
            card.customerId === profileData?.userStripeId
          ) {
            paymentRequestStatus = 'approved'
          } else {
            paymentRequestStatus =
              card.pmId && organizer?.id && user
                ? getPaymentRequestStatus(card.pmId, organizer?.id, user)
                : null
          }

          return (
            <PaymentCardItem
              key={index}
              type={card.type}
              pmId={card.pmId}
              card={card}
              cardNumber={card.cardNumber}
              bankName={(card as IUserACH)?.bankName}
              selectedCard={selectedCard}
              setSelectedCard={setSelectedCard}
              setPaymentMethods={setPaymentMethods}
              paymentMethods={paymentMethods}
              organiserProfile={organizer}
              user={user}
              paymentRequestStatus={paymentRequestStatus}
              isDefault={
                selectedPaymentMethod === 'delayed'
                  ? selectedCard.pmId === card.pmId
                  : user?.userStripeDefaultPmId === card.pmId
              }
            />
          )
        }
      })}
      <AddNew icon={IconCreditCard} title="Add a new card" onClick={openAddNewCardModal} />
      {openAddNewBankModal && availablePaymentMethods.includes('ach') && (
        <AddNew icon={IconBank} title="Connect new bank account" onClick={openAddNewBankModal} />
      )}
    </>
  )
}
